import { useState } from 'react';
import { Trash2, X, Check, Image, Plus, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  useCreateChecklistItem,
  useUpdateChecklistItem,
  useDeleteChecklistItem,
  useLinkChecklistPhotos,
  useUnlinkChecklistPhoto,
  type ChecklistItem,
  type VisitPhoto,
} from '@/hooks/useSiteVisits';
import { useSiteVisitDefaults } from '@/hooks/useSettings';
import type { ChecklistStatusOption } from '@/lib/schemas';

const DEFAULT_STATUSES: ChecklistStatusOption[] = [
  { value: 'info_only', label: 'Info Only' },
  { value: 'contractor_action', label: 'Contractor Action' },
  { value: 'architect_action', label: 'Architect Action' },
];

interface Props {
  visitId: string;
  checklistItems: ChecklistItem[];
  /** Photos available for evidence linking */
  photos: VisitPhoto[];
  isEditable: boolean;
}

export function SiteVisitChecklistEditor({ visitId, checklistItems, photos, isEditable }: Props) {
  const createItem = useCreateChecklistItem();
  const updateItem = useUpdateChecklistItem();
  const deleteItem = useDeleteChecklistItem();
  const linkPhotos = useLinkChecklistPhotos();
  const unlinkPhoto = useUnlinkChecklistPhoto();
  const { data: siteVisitDefaults } = useSiteVisitDefaults();

  const statuses: ChecklistStatusOption[] = siteVisitDefaults?.checklist_statuses ?? DEFAULT_STATUSES;
  const defaultStatus = statuses[0]?.value ?? 'info_only';

  const statusLabel = (value: string) =>
    statuses.find((s) => s.value === value)?.label ?? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const [newItemDesc, setNewItemDesc] = useState('');
  const [editingDescId, setEditingDescId] = useState<string | null>(null);
  const [editDescValue, setEditDescValue] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const [deleteConfirmItemId, setDeleteConfirmItemId] = useState<string | null>(null);
  const [addingPhotoItemId, setAddingPhotoItemId] = useState<string | null>(null);
  const [pendingPhotoIds, setPendingPhotoIds] = useState<string[]>([]);

  const handleAddItem = async () => {
    if (!newItemDesc.trim() || createItem.isPending) return;
    await createItem.mutateAsync({ visitId, item: { description: newItemDesc.trim(), status: defaultStatus } });
    setNewItemDesc('');
  };

  const openDescEdit = (item: ChecklistItem) => {
    setEditingDescId(item.id);
    setEditDescValue(item.description);
  };

  const saveDescEdit = async (itemId: string) => {
    if (!editDescValue.trim()) return;
    await updateItem.mutateAsync({ visitId, itemId, data: { description: editDescValue.trim() } });
    setEditingDescId(null);
  };

  const cancelDescEdit = () => setEditingDescId(null);

  const openNoteEdit = (item: ChecklistItem) => {
    setEditingNoteId(item.id);
    setNoteValue(item.notes || '');
  };

  const saveNote = async (itemId: string) => {
    await updateItem.mutateAsync({ visitId, itemId, data: { notes: noteValue } });
    setEditingNoteId(null);
  };

  const handleDeleteConfirmed = () => {
    if (!deleteConfirmItemId) return;
    deleteItem.mutate({ visitId, itemId: deleteConfirmItemId });
    setDeleteConfirmItemId(null);
  };

  const openPhotoPicker = (itemId: string) => {
    setAddingPhotoItemId(itemId);
    setPendingPhotoIds([]);
  };

  const closePhotoPicker = () => {
    setAddingPhotoItemId(null);
    setPendingPhotoIds([]);
  };

  const togglePendingPhoto = (photoId: string) => {
    setPendingPhotoIds((prev) =>
      prev.includes(photoId) ? prev.filter((id) => id !== photoId) : [...prev, photoId]
    );
  };

  const handleLinkPhotos = (item: ChecklistItem, photoIds: string[]) => {
    if (!photoIds.length) return;
    linkPhotos.mutate({ visitId, itemId: item.id, photoIds });
    closePhotoPicker();
  };

  const handleUnlinkPhoto = (item: ChecklistItem, photoId: string) => {
    unlinkPhoto.mutate({ visitId, itemId: item.id, photoId });
  };

  const availablePhotos = (item: ChecklistItem) =>
    photos.filter((p) => !item.photo_ids.includes(p.id));

  const photoLabel = (photo: VisitPhoto) =>
    photo.caption ? photo.caption.slice(0, 20) : photo.file_name.slice(0, 20);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Checklist ({checklistItems.length})
        </h4>
      </div>

      <div className="space-y-0">
        {checklistItems.map((item) => (
          <div key={item.id} className="group border-b border-muted last:border-0 py-2 space-y-1">
            {/* Main row */}
            <div className="flex items-center gap-2">
              {/* Status dropdown */}
              {isEditable ? (
                <Select
                  value={item.status}
                  onValueChange={(val) =>
                    updateItem.mutate({ visitId, itemId: item.id, data: { status: val } })
                  }
                  disabled={!isEditable}
                >
                  <SelectTrigger className="h-7 w-[140px] text-[10px] flex-shrink-0 px-2">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="text-xs">
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="flex-shrink-0 text-[10px] font-medium text-muted-foreground bg-muted rounded px-2 py-0.5 min-w-[80px] text-center">
                  {statusLabel(item.status)}
                </span>
              )}

              {/* Description */}
              {editingDescId === item.id ? (
                <div className="flex items-center gap-1 flex-1">
                  <Input
                    value={editDescValue}
                    onChange={(e) => setEditDescValue(e.target.value)}
                    className="h-6 text-sm flex-1"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveDescEdit(item.id);
                      if (e.key === 'Escape') cancelDescEdit();
                    }}
                  />
                  <button
                    className="p-0.5 text-green-700 hover:bg-green-100 rounded min-h-[28px] min-w-[28px] flex items-center justify-center"
                    onClick={() => saveDescEdit(item.id)}
                    title="Save"
                  >
                    <Check className="w-3 h-3" />
                  </button>
                  <button
                    className="p-0.5 rounded hover:bg-muted min-h-[28px] min-w-[28px] flex items-center justify-center"
                    onClick={cancelDescEdit}
                    title="Cancel"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <>
                  <span className="text-sm flex-1">
                    {item.description}
                  </span>
                  {isEditable && (
                    <button
                      className="opacity-60 hover:opacity-100 focus-visible:opacity-100 transition-opacity p-0.5 hover:text-primary flex-shrink-0 min-h-[28px] min-w-[28px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                      onClick={() => openDescEdit(item)}
                      title="Edit description"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}

              {/* Photo count badge (read-only) */}
              {!isEditable && item.photo_ids.length > 0 && (
                <span
                  className="flex items-center gap-0.5 text-xs text-muted-foreground flex-shrink-0"
                  title={`${item.photo_ids.length} photo(s)`}
                >
                  <Image className="w-3.5 h-3.5" />
                  {item.photo_ids.length}
                </span>
              )}

              {/* Delete (opens confirm) */}
              {isEditable && (
                <button
                  className="opacity-60 hover:opacity-100 focus-visible:opacity-100 transition-opacity p-0.5 hover:text-destructive flex-shrink-0 min-h-[28px] min-w-[28px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  onClick={() => setDeleteConfirmItemId(item.id)}
                  title="Delete item"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Notes row */}
            {editingNoteId === item.id ? (
              <div className="flex gap-1 pl-[152px]">
                <Input
                  value={noteValue}
                  onChange={(e) => setNoteValue(e.target.value)}
                  placeholder="Notes..."
                  className="h-7 text-xs flex-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveNote(item.id);
                    if (e.key === 'Escape') setEditingNoteId(null);
                  }}
                />
                <Button size="sm" className="h-7 px-2 text-xs" onClick={() => saveNote(item.id)}>
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => setEditingNoteId(null)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="pl-[152px]">
                {item.notes ? (
                  <span
                    className={cn(
                      'text-xs text-muted-foreground italic',
                      isEditable && 'cursor-pointer hover:text-foreground',
                    )}
                    onClick={() => isEditable && openNoteEdit(item)}
                    title={isEditable ? 'Click to edit note' : undefined}
                  >
                    {item.notes}
                  </span>
                ) : isEditable ? (
                  <button
                    className="text-xs text-muted-foreground/40 hover:text-muted-foreground italic"
                    onClick={() => openNoteEdit(item)}
                  >
                    Add note...
                  </button>
                ) : null}
              </div>
            )}

            {/* Photo evidence (editable: linked thumbnails + add picker) */}
            {isEditable && (
              <div className="pl-[152px] space-y-1">
                {item.photo_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    {item.photo_ids.map((pid) => {
                      const photo = photos.find((p) => p.id === pid);
                      return (
                        <span
                          key={pid}
                          className="inline-flex items-center gap-1 text-[10px] bg-muted rounded px-1.5 py-0.5 text-muted-foreground"
                          title={photo ? photoLabel(photo) : pid}
                        >
                          <Image className="w-3 h-3 flex-shrink-0" />
                          {photo ? photoLabel(photo) : pid.slice(0, 8)}
                          <button
                            className="hover:text-destructive flex-shrink-0"
                            onClick={() => handleUnlinkPhoto(item, pid)}
                            title="Remove photo"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {addingPhotoItemId === item.id ? (
                  <div className="mt-1 border border-muted rounded p-2 space-y-1 bg-background">
                    {availablePhotos(item).map((p) => (
                      <label
                        key={p.id}
                        className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5"
                      >
                        <input
                          type="checkbox"
                          checked={pendingPhotoIds.includes(p.id)}
                          onChange={() => togglePendingPhoto(p.id)}
                          className="rounded"
                        />
                        <Image className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="truncate">{photoLabel(p)}</span>
                      </label>
                    ))}
                    <div className="flex gap-1 pt-1 border-t border-muted">
                      <button
                        className="text-xs bg-primary text-primary-foreground rounded px-2 py-0.5 disabled:opacity-40"
                        disabled={pendingPhotoIds.length === 0}
                        onClick={() => handleLinkPhotos(item, pendingPhotoIds)}
                      >
                        Link{pendingPhotoIds.length > 0 ? ` (${pendingPhotoIds.length})` : ''}
                      </button>
                      <button
                        className="text-xs text-muted-foreground hover:text-foreground px-1"
                        onClick={closePhotoPicker}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : availablePhotos(item).length > 0 ? (
                  <button
                    className="flex items-center gap-0.5 text-[10px] text-muted-foreground/50 hover:text-muted-foreground"
                    onClick={() => openPhotoPicker(item.id)}
                  >
                    <Plus className="w-3 h-3" />
                    Add photo
                  </button>
                ) : item.photo_ids.length === 0 ? (
                  <span className="text-[10px] text-muted-foreground/30 italic">No photos uploaded</span>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add checklist item */}
      {isEditable && (
        <div className="flex gap-2 mt-3">
          <Input
            placeholder="Add checklist item..."
            value={newItemDesc}
            onChange={(e) => setNewItemDesc(e.target.value)}
            className="h-8 text-sm"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddItem(); } }}
          />
          <Button
            size="sm"
            className="h-8"
            onClick={handleAddItem}
            disabled={!newItemDesc.trim() || createItem.isPending}
          >
            Add
          </Button>
        </div>
      )}

      {/* Delete confirm dialog */}
      <AlertDialog open={!!deleteConfirmItemId} onOpenChange={() => setDeleteConfirmItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Checklist Item</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this checklist item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirmed}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
